import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, Send, Loader2, Copy, Mail, BadgeCheck, ChevronDown } from "lucide-react";
import { rememberReferralCode } from "@/lib/referrals";
import { SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from "@/constants/support";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRY_CODES = [
  { name: "India", dialCode: "+91" },
  { name: "United States", dialCode: "+1" },
  { name: "Canada", dialCode: "+1" },
  { name: "United Kingdom", dialCode: "+44" },
  { name: "United Arab Emirates", dialCode: "+971" },
  { name: "Saudi Arabia", dialCode: "+966" },
  { name: "Qatar", dialCode: "+974" },
  { name: "Kuwait", dialCode: "+965" },
  { name: "Oman", dialCode: "+968" },
  { name: "Bahrain", dialCode: "+973" },
  { name: "Australia", dialCode: "+61" },
  { name: "New Zealand", dialCode: "+64" },
  { name: "Singapore", dialCode: "+65" },
  { name: "Malaysia", dialCode: "+60" },
  { name: "Indonesia", dialCode: "+62" },
  { name: "Thailand", dialCode: "+66" },
  { name: "Vietnam", dialCode: "+84" },
  { name: "Philippines", dialCode: "+63" },
  { name: "Japan", dialCode: "+81" },
  { name: "South Korea", dialCode: "+82" },
  { name: "China", dialCode: "+86" },
  { name: "Hong Kong", dialCode: "+852" },
  { name: "Taiwan", dialCode: "+886" },
  { name: "Pakistan", dialCode: "+92" },
  { name: "Bangladesh", dialCode: "+880" },
  { name: "Sri Lanka", dialCode: "+94" },
  { name: "Nepal", dialCode: "+977" },
  { name: "Bhutan", dialCode: "+975" },
  { name: "Germany", dialCode: "+49" },
  { name: "France", dialCode: "+33" },
  { name: "Italy", dialCode: "+39" },
  { name: "Spain", dialCode: "+34" },
  { name: "Portugal", dialCode: "+351" },
  { name: "Netherlands", dialCode: "+31" },
  { name: "Belgium", dialCode: "+32" },
  { name: "Switzerland", dialCode: "+41" },
  { name: "Ireland", dialCode: "+353" },
  { name: "Sweden", dialCode: "+46" },
  { name: "Norway", dialCode: "+47" },
  { name: "Denmark", dialCode: "+45" },
  { name: "Finland", dialCode: "+358" },
  { name: "Greece", dialCode: "+30" },
  { name: "Poland", dialCode: "+48" },
  { name: "Turkey", dialCode: "+90" },
  { name: "Russia", dialCode: "+7" },
  { name: "Israel", dialCode: "+972" },
  { name: "South Africa", dialCode: "+27" },
  { name: "Nigeria", dialCode: "+234" },
  { name: "Kenya", dialCode: "+254" },
  { name: "Egypt", dialCode: "+20" },
  { name: "Ghana", dialCode: "+233" },
  { name: "Morocco", dialCode: "+212" },
  { name: "Brazil", dialCode: "+55" },
  { name: "Mexico", dialCode: "+52" },
  { name: "Argentina", dialCode: "+54" },
  { name: "Chile", dialCode: "+56" },
  { name: "Colombia", dialCode: "+57" },
];

const Unlock = () => {
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState("phone");
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]);
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);
  const [purchase, setPurchase] = useState(null);
  const [link, setLink] = useState(null);
  const [expires, setExpires] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [claimedAffiliate, setClaimedAffiliate] = useState(null);

  useEffect(() => {
    setReferralCode(rememberReferralCode(window.location.search));
  }, []);

  const phoneForRequest = () => {
    const rawPhone = identifier.trim();
    if (rawPhone.startsWith("+")) return rawPhone;
    const digits = rawPhone.replace(/\D/g, "");
    const dialDigits = selectedCountry.dialCode.replace(/\D/g, "");
    const nationalDigits = digits.startsWith("0") && digits.length > 10 ? digits.slice(1) : digits;
    return digits.startsWith(dialDigits) && digits.length > 10
      ? `+${digits}`
      : `${selectedCountry.dialCode}${nationalDigits}`;
  };

  useEffect(() => {
    if (!link) return;
    const iv = setInterval(() => setExpires((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(iv);
  }, [link]);

  const verifyPurchase = async () => {
    setBusy(true);
    setError("");
    try {
      const identity = identifierType === "phone"
        ? { phone: phoneForRequest() }
        : { email: identifier.trim().toLowerCase() };
      const r = await axios.post(`${API}/access/lookup`, identity);
      const claimResponse = await axios.post(`${API}/access/claim`, {
        ...identity,
        market: "gold",
        ref: referralCode.trim(),
      });
      setPurchase(r.data);
      setLink(claimResponse.data.bot_url);
      setExpires(claimResponse.data.expires_in_seconds);
      setClaimedAffiliate(claimResponse.data.affiliate ? {
        name: claimResponse.data.affiliate_name || claimResponse.data.affiliate,
      } : null);
      toast.success("Purchase verified · bot link ready");
    } catch (e) {
      if (e.response?.status === 404) {
        setError(`No purchase found for this ${identifierType === "phone" ? "phone number" : "email"} yet. Just paid? Confirmation can take 1–2 minutes — wait a moment and verify again. Still stuck? Call ${SUPPORT_PHONE_DISPLAY}.`);
      } else {
        setError(e.response?.data?.detail || "Verification failed — try again.");
      }
    }
    setBusy(false);
  };

  const copy = () => {
    navigator.clipboard?.writeText(link);
    toast.success("Bot link copied — bot session valid 30 minutes");
  };

  const mm = String(Math.floor(expires / 60)).padStart(2, "0");
  const ss = String(expires % 60).padStart(2, "0");

  return (
    <main className="horizon-page relative min-h-screen overflow-x-hidden py-10" data-testid="unlock-page">
      <div className="relative z-10 mx-auto max-w-4xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="mt-5 font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500" data-testid="unlock-eyebrow">
            Payment complete · Welcome back
          </p>
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a] sm:text-4xl lg:text-5xl">
            Claim your <span className="font-accent text-grad-aurora">50crclub</span> seat
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-base text-slate-600">
            Verify the country-coded phone number you paid with and the Telegram bot hands you a private
            5-minute join link. That's it.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="glass unlock-panel-verify mt-12 rounded-3xl p-8"
          data-testid="unlock-step-verify"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0a0a0a] font-mono2 text-xs font-semibold text-white">1</span>
            <h2 className="font-sub text-xl font-semibold uppercase tracking-wide text-[#0a0a0a]">Verify your purchase</h2>
          </div>

          {!purchase ? (
            <div className="mt-6">
              <div className="mb-4 flex gap-2" role="group" aria-label="Verification method">
                <button
                  type="button"
                  data-testid="verify-by-phone"
                  onClick={() => { setIdentifierType("phone"); setIdentifier(""); setError(""); }}
                  className={`rounded-full border px-4 py-2 font-mono2 text-[10px] uppercase tracking-widest ${identifierType === "phone" ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 bg-white text-slate-500"}`}
                >Phone number</button>
                <button
                  type="button"
                  data-testid="verify-by-email"
                  onClick={() => { setIdentifierType("email"); setIdentifier(""); setError(""); }}
                  className={`rounded-full border px-4 py-2 font-mono2 text-[10px] uppercase tracking-widest ${identifierType === "email" ? "border-[#0a0a0a] bg-[#0a0a0a] text-white" : "border-black/10 bg-white text-slate-500"}`}
                >Older purchase · email</button>
              </div>
              <label htmlFor="verify-identifier" className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                {identifierType === "phone" ? "Phone number used at payment" : "Email used at payment"}
              </label>
              <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                {identifierType === "phone" ? (
                  <div className="flex min-w-0 flex-1 rounded-xl border border-black/10 bg-white focus-within:border-black/50">
                    <Popover open={countryPickerOpen} onOpenChange={setCountryPickerOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          aria-label="Search and select country calling code"
                          aria-expanded={countryPickerOpen}
                          data-testid="phone-country-selector"
                          className="flex w-[142px] shrink-0 items-center justify-between gap-2 rounded-l-xl bg-black/[0.03] px-3 py-3.5 text-left font-mono2 text-xs text-[#0a0a0a] sm:w-[160px]"
                        >
                          <span className="truncate">{selectedCountry.name} {selectedCountry.dialCode}</span>
                          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-[280px] p-0">
                        <Command>
                          <CommandInput placeholder="Search country or code..." />
                          <CommandList>
                            <CommandEmpty>No country found.</CommandEmpty>
                            {COUNTRY_CODES.map((country) => (
                              <CommandItem
                                key={`${country.name}-${country.dialCode}`}
                                value={`${country.name} ${country.dialCode}`}
                                onSelect={() => {
                                  setSelectedCountry(country);
                                  setCountryPickerOpen(false);
                                }}
                                className="py-2.5"
                              >
                                <span>{country.name}</span>
                                <span className="ml-auto font-mono2 text-xs text-slate-500">{country.dialCode}</span>
                              </CommandItem>
                            ))}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <input
                      id="verify-identifier"
                      data-testid="verify-phone-input"
                      type="tel"
                      autoComplete="tel-national"
                      value={identifier}
                      onChange={(event) => setIdentifier(event.target.value)}
                      placeholder="98765 43210"
                      className="min-w-0 flex-1 rounded-r-xl border-l border-black/10 bg-transparent px-3 py-3.5 font-mono2 text-sm text-[#0a0a0a] outline-none placeholder:text-slate-400"
                    />
                  </div>
                ) : (
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input
                      id="verify-identifier"
                      data-testid="verify-email-input"
                      type="email"
                      autoComplete="email"
                      value={identifier}
                      onChange={(event) => setIdentifier(event.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-xl border border-black/10 bg-white py-3.5 pl-11 pr-4 font-mono2 text-sm text-[#0a0a0a] outline-none transition-colors placeholder:text-slate-400 focus:border-[#0a0a0a]/50"
                    />
                  </div>
                )}
                <button
                  data-testid="verify-phone-button"
                  onClick={verifyPurchase}
                  disabled={busy || (identifierType === "phone" ? identifier.replace(/\D/g, "").length < 10 : !identifier.includes("@"))}
                  className="btn-main flex items-center justify-center gap-2.5 rounded-xl px-7 py-3.5 font-sub text-sm font-bold uppercase tracking-wide disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
                  Verify
                </button>
              </div>
              {error && (
                <p data-testid="verify-error" className="mt-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                  {error}
                </p>
              )}
            </div>
          ) : (
            <div className="mt-6 flex items-center gap-4 rounded-2xl border border-black/10 bg-black/[0.03] p-5" data-testid="purchase-card">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0a0a0a]">
                <Check className="h-5 w-5 text-white" strokeWidth={3} />
              </span>
              <div>
                <p className="font-sub text-sm font-semibold text-[#0a0a0a]">
                  {purchase.buyer_name ? `${purchase.buyer_name} — ` : ""}{purchase.offering || "50crclub"} · ₹{purchase.amount}
                </p>
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
                  {purchase.buyer_phone || purchase.buyer_email || identifier} · verified
                </p>
                {purchase.buyer_phone && purchase.buyer_email && (
                  <p className="mt-1 text-xs text-slate-500">Payment email retained: {purchase.buyer_email}</p>
                )}
              </div>
            </div>
          )}
          {purchase && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="unlock-verification-status">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-emerald-700">
                <Check className="h-3.5 w-3.5" /> Payment verified
              </span>
              <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider ${claimedAffiliate || purchase.affiliate || referralCode ? "border-sky-300 bg-sky-50 text-sky-700" : "border-black/10 bg-white text-slate-500"}`}>
                {claimedAffiliate
                  ? `Seller credited · ${claimedAffiliate.name}`
                  : purchase.affiliate
                    ? `Seller credit · ${purchase.affiliate_name || purchase.affiliate}`
                  : referralCode
                    ? "Seller attribution attached"
                    : "Direct purchase"}
              </span>
              {link && (
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-emerald-700">
                  <Check className="h-3.5 w-3.5" /> Ready to connect bot
                </span>
              )}
            </div>
          )}
        </motion.div>

        {link && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="glass unlock-panel-telegram mt-8 rounded-3xl p-8"
            data-testid="unlock-step-telegram"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0a0a0a] font-mono2 text-xs font-semibold text-white">2</span>
              <h2 className="font-sub text-xl font-semibold uppercase tracking-wide text-[#0a0a0a]">Your Telegram bot link</h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-slate-600">
              Open the bot link below and press Start. The bot will ask you to confirm the phone number
              used for payment, then send your single-use group invite, valid for 5 minutes.
            </p>
            <div className="mt-6 rounded-2xl border border-black/10 bg-black/[0.03] p-5" data-testid="telegram-link-panel">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    Bot session expires in {mm}:{ss}
                  </p>
                  <p className="mt-2 truncate font-mono2 text-sm text-[#0a0a0a]" data-testid="telegram-link-text">{link}</p>
                </div>
                <div className="flex gap-3">
                  <button
                    data-testid="copy-telegram-link"
                    onClick={copy}
                    className="flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 font-sub text-xs font-semibold uppercase tracking-wide text-slate-600 transition-colors hover:border-[#0a0a0a] hover:text-[#0a0a0a]"
                  >
                    <Copy className="h-3.5 w-3.5" /> Copy
                  </button>
                  <a
                    data-testid="open-telegram-bot"
                    href={link}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-main flex items-center gap-2 rounded-full px-5 py-2.5 font-sub text-xs font-bold uppercase tracking-wide"
                  >
                    <Send className="h-3.5 w-3.5" /> Open Bot
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <p className="mt-10 text-center font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500" data-testid="unlock-support-line">
          Any issue? Call <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#0a0a0a] underline-offset-4 hover:underline">{SUPPORT_PHONE_DISPLAY}</a> or message the <a href="https://t.me/team50CrClub_bot" target="_blank" rel="noreferrer" className="text-[#0a0a0a] underline-offset-4 hover:underline">Telegram desk</a>
        </p>
        <p className="mt-4 text-center">
          <Link to="/" data-testid="unlock-home-link" className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-[#0a0a0a]">
            ← Back to home
          </Link>
        </p>
      </div>
    </main>
  );
};

export default Unlock;
